import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { select, Store } from '@ngrx/store';
import {
  AppState,
  CategoryFilterOption,
  FilterState,
  LivingSpaceRange,
  PriceRange,
  PropertySpaceRange,
  RoomCountRange,
  SubcategoryOptionsMap,
  UsageSpaceRange,
} from '@immosky/types';
import {
  resetCategoryFilter,
  resetLivingSpaceFilter,
  resetPriceFilter,
  resetPropertySpaceFilter,
  resetRoomCountFilter,
  resetUsageSpaceFilter,
  selectFilterState,
  updateFilter,
} from '@immosky/object-search-filter';
import {
  ActiveAdditionalFilters,
  DesktopFilterService,
} from '../../../services/desktop-filter.service';

@Component({
  selector: 'app-additional-filters-dropdown',
  templateUrl: './additional-filters-dropdown.component.html',
  styleUrls: ['./additional-filters-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdditionalFiltersDropdownComponent implements OnDestroy {
  private subscriptions: Subscription[] = [];

  filterState!: FilterState;
  activeAdditionalFilters!: ActiveAdditionalFilters;

  currentCategory!: CategoryFilterOption;
  currentSubcategory!: SubcategoryOptionsMap;
  currentPrice!: PriceRange;
  currentRoomCount!: RoomCountRange;
  currentLivingSpace!: LivingSpaceRange;
  currentUsageSpace!: UsageSpaceRange;
  currentPropertySpace!: PropertySpaceRange;

  hasValue = false;

  constructor(
    private cdRef: ChangeDetectorRef,
    private store: Store<AppState>,
    private desktopFilterService: DesktopFilterService
  ) {
    const activeAdditionalFiltersSubscription =
      this.desktopFilterService.layoutUpdateRequest.subscribe(() => {
        this.activeAdditionalFilters =
          this.desktopFilterService.activeAdditionalFilters;
        this.hasValue = this.checkHasValue();
        this.cdRef.markForCheck();
      });
    this.activeAdditionalFilters =
      this.desktopFilterService.activeAdditionalFilters;

    const filterSubscription = this.store
      .pipe(select(selectFilterState))
      .subscribe((filterState: FilterState) => {
        this.filterState = filterState;
        this.currentCategory = filterState.category.main.filter;
        this.currentSubcategory = filterState.category.sub.filter;
        this.currentPrice = filterState.price.filter;
        this.currentRoomCount = filterState.roomCount.filter;
        this.currentLivingSpace = filterState.livingSpace.filter;
        this.currentUsageSpace = filterState.usageSpace.filter;
        this.currentPropertySpace = filterState.propertySpace.filter;

        this.hasValue = this.checkHasValue();

        this.cdRef.markForCheck();
      });

    this.subscriptions.push(
      filterSubscription,
      activeAdditionalFiltersSubscription
    );
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  checkHasValue(): boolean {
    let isActive = false;

    if (this.activeAdditionalFilters.get('category')) {
      isActive = this.filterState.category.main.isActive || isActive;
    }

    if (this.activeAdditionalFilters.get('price')) {
      isActive = this.filterState.price.isActive || isActive;
    }

    if (this.activeAdditionalFilters.get('roomCount')) {
      isActive = this.filterState.roomCount.isActive || isActive;
    }

    if (this.activeAdditionalFilters.get('livingSpace')) {
      isActive = this.filterState.livingSpace.isActive || isActive;
    }

    if (this.activeAdditionalFilters.get('usageSpace')) {
      isActive = this.filterState.usageSpace.isActive || isActive;
    }

    if (this.activeAdditionalFilters.get('propertySpace')) {
      isActive = this.filterState.propertySpace.isActive || isActive;
    }

    return isActive;
  }

  applyChanges() {
    this.store.dispatch(
      updateFilter({
        mainCategory: this.currentCategory,
        subcategory: this.currentSubcategory,
        price: this.currentPrice,
        roomCount: this.currentRoomCount,
        livingSpace: this.currentLivingSpace,
        usageSpace: this.currentUsageSpace,
        propertySpace: this.currentPropertySpace,
      })
    );
  }

  onCategoryChange(filter: CategoryFilterOption) {
    this.currentCategory = filter;
  }

  onPriceChange(filter: PriceRange) {
    this.currentPrice = filter;
  }

  onRoomCountChange(filter: RoomCountRange) {
    this.currentRoomCount = filter;
  }

  onLivingSpaceChange(filter: LivingSpaceRange) {
    this.currentLivingSpace = filter;
  }

  onUsageSpaceChange(filter: UsageSpaceRange) {
    this.currentUsageSpace = filter;
  }

  onPropertySpaceChange(filter: PropertySpaceRange) {
    this.currentPropertySpace = filter;
  }

  onSubcategoryChange(filter: SubcategoryOptionsMap) {
    this.currentSubcategory = filter;
  }

  reset() {
    if (this.activeAdditionalFilters.get('category')) {
      this.store.dispatch(resetCategoryFilter());
    }
    if (this.activeAdditionalFilters.get('price')) {
      this.store.dispatch(resetPriceFilter());
    }
    if (this.activeAdditionalFilters.get('roomCount')) {
      this.store.dispatch(resetRoomCountFilter());
    }
    if (this.activeAdditionalFilters.get('livingSpace')) {
      this.store.dispatch(resetLivingSpaceFilter());
    }
    if (this.activeAdditionalFilters.get('usageSpace')) {
      this.store.dispatch(resetUsageSpaceFilter());
    }
    if (this.activeAdditionalFilters.get('propertySpace')) {
      this.store.dispatch(resetPropertySpaceFilter());
    }
  }
}
